import React, { useState } from 'react';
import './ReminderControls.css';

const REMINDER_OPTIONS = [
  { value: 24, label: '24 hours before' },
  { value: 48, label: '2 days before' },
  { value: 72, label: '3 days before' },
  { value: 96, label: '4 days before' },
  { value: 120, label: '5 days before' },
  { value: 144, label: '6 days before' },
  { value: 168, label: '7 days before' },
  { value: 192, label: '8 days before' },
  { value: 216, label: '9 days before' },
  { value: 240, label: '10 days before' },
  { value: 264, label: '11 days before' },
  { value: 288, label: '12 days before' },
  { value: 312, label: '13 days before' },
  { value: 336, label: '14 days before' }
];

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ReminderControls = ({ enabled, reminders = [], onUpdate, settings }) => {
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  const handleToggle = () => {
    // If we're enabling reminders and there are no reminders set,
    // add a default 24-hour reminder
    const newReminders = !enabled && reminders.length === 0 ? [24] : reminders;
    
    onUpdate({
      ...settings,
      reminderEnabled: !enabled,
      reminders: newReminders,
      notifications: {
        ...settings.notifications,
        bookingConfirmation: true,
        appointmentReminders: !enabled, // sync with reminder toggle
        schedulingUpdates: true
      }
    });
  };

  const handleReminderChange = (index, value) => {
    const newReminders = [...reminders];
    newReminders[index] = Number(value);
    
    // Sort reminders in ascending order (earliest to latest)
    newReminders.sort((a, b) => a - b);
    
    onUpdate({ 
      ...settings,
      reminderEnabled: enabled,
      reminders: newReminders,
      notifications: {
        ...settings.notifications,
        bookingConfirmation: true,
        appointmentReminders: true,
        schedulingUpdates: true
      }
    });
  };

  const handleAddReminder = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    
    if (reminders.length < 3) {
      const availableOptions = REMINDER_OPTIONS
        .filter(option => !reminders.includes(option.value));
      
      if (availableOptions.length > 0) {
        const newReminders = [...reminders, availableOptions[0].value].sort((a, b) => a - b);
        onUpdate({
          ...settings,
          reminderEnabled: enabled,
          reminders: newReminders,
          notifications: {
            ...settings.notifications,
            bookingConfirmation: true,
            appointmentReminders: true,
            schedulingUpdates: true
          }
        });
      }
    }
  };

  const handleDeleteReminder = (index) => {
    const newReminders = reminders.filter((_, i) => i !== index);
    
    // If deleting the last reminder, disable the reminder feature
    const shouldDisableReminders = newReminders.length === 0;
    
    onUpdate({
      ...settings,
      reminderEnabled: shouldDisableReminders ? false : enabled,
      reminders: newReminders,
      notifications: {
        ...settings.notifications,
        bookingConfirmation: true,
        appointmentReminders: shouldDisableReminders ? false : true,
        schedulingUpdates: true
      }
    });
  };

  const handleRequestDeleteReminder = (index) => {
    setPendingDeleteIndex(index);
  };

  const handleCancelDeleteReminder = () => {
    setPendingDeleteIndex(null);
  };

  const handleConfirmDeleteReminder = () => {
    if (pendingDeleteIndex === null) {
      return;
    }

    handleDeleteReminder(pendingDeleteIndex);
    setPendingDeleteIndex(null);
  };

  return (
    <div className="email-reminder-controls">
      <div className="email-reminder-controls__enable-row">
        <div className="email-reminder-controls__enable-header">
          <h3 className="email-reminder-controls__title">Enable automatic reminders</h3>

          <label
            className="email-reminder-controls__checkbox"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={handleToggle}
              aria-label="Enable automatic reminders"
            />
          </label>
        </div>

        <p className="email-reminder-controls__description">
          Send reminder emails automatically before appointments.
        </p>
      </div>

      {enabled && (
        <div className="email-reminder-controls__list">
          {reminders.map((reminder, index) => (
            <div key={index} className="email-reminder-controls__row">
              <label className="email-reminder-controls__select-wrap">
                <span>Send reminder</span>
                <select
                  value={reminder}
                  onChange={(e) => handleReminderChange(index, e.target.value)}
                >
                  {REMINDER_OPTIONS.map((option) => (
                    <option
                      key={option.value} 
                      value={option.value}
                      disabled={reminders.includes(option.value) && reminder !== option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="email-reminder-controls__delete"
                onClick={() => handleRequestDeleteReminder(index)}
                aria-label="Remove reminder"
                title="Remove reminder"
              >
                <TrashIcon />
              </button>
            </div>
          ))}
          
          {reminders.length < 3 && (
            <button
              type="button"
              className="email-reminder-controls__add"
              onClick={handleAddReminder}
            >
              Add Reminder
            </button>
          )}
        </div>
      )}

      {pendingDeleteIndex !== null && (
        <div className="email-reminder-controls__confirm-modal" role="dialog" aria-modal="true" aria-label="Remove reminder">
          <div className="email-reminder-controls__confirm-backdrop" onClick={handleCancelDeleteReminder} />
          <div className="email-reminder-controls__confirm-panel">
            <h4>Remove reminder?</h4>
            <p>This will delete that reminder timing from your schedule.</p>
            <div className="email-reminder-controls__confirm-actions">
              <button
                type="button"
                className="email-reminder-controls__confirm-button email-reminder-controls__confirm-button--secondary"
                onClick={handleCancelDeleteReminder}
              >
                Cancel
              </button>
              <button
                type="button"
                className="email-reminder-controls__confirm-button email-reminder-controls__confirm-button--danger"
                onClick={handleConfirmDeleteReminder}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderControls; 